import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem, EmptyState, LoadingState } from '../components/ui';
import { alert } from '../utils/toast';
import type { NotificationTemplate } from '../types';
import { Mail, Edit2, Code, X, Loader2, FileText, CheckCircle2 } from 'lucide-react';

export const NotificationTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [formData, setFormData] = useState({
    subject: '',
    body_html: '',
    body_text: '',
  });

  // Fechar modal com tecla Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: async () => {
      const response = await api.get<NotificationTemplate[]>('/admin/notification-templates');
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      subject: string;
      body_html: string;
      body_text: string;
    }) => {
      const { id, ...updateData } = data;
      await api.put(`/admin/notification-templates/${id}`, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      closeModal();
      void alert.success('Atualizado!', 'Modelo de notificação atualizado com sucesso!');
    },
  });

  const handleEdit = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setFormData({
      subject: template.subject,
      body_html: template.body_html,
      body_text: template.body_text,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, ...formData });
    }
  };

  const closeModal = () => {
    setEditingTemplate(null);
    setFormData({ subject: '', body_html: '', body_text: '' });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem>Modelos de Notificação</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={2} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Modelos de Notificação</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center">
          <Mail className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">
            Modelos de Notificação
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Personalize os templates de email e SMS enviados automaticamente pelo sistema.
          </p>
        </div>
      </div>

      {templates && templates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-between items-start mb-3 gap-3">
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 mt-0.5">
                      <FileText className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white font-mono text-sm">
                        {template.template_key}
                      </h3>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        {template.subject}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(template)}
                    className="btn-base btn-secondary text-xs px-3 py-1.5 font-semibold flex items-center gap-1.5 shrink-0"
                  >
                    <Edit2 className="w-3.5 h-3.5" /> Editar
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 mt-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
                  Variáveis Dinâmicas
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {template.variables.map((v) => (
                    <span
                      key={v}
                      className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono text-[11px] border border-slate-200 dark:border-slate-700"
                    >
                      {'{'}
                      {v}
                      {'}'}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center shadow-sm">
          <EmptyState
            title="Nenhum modelo encontrado"
            description="Não existem modelos de notificação registados no sistema."
          />
        </div>
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex justify-between items-center pb-4 border-b border-slate-100 dark:border-slate-800 mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-primary-500" /> Editar Modelo:{' '}
                <span className="font-mono text-primary-600 dark:text-primary-400">
                  {editingTemplate.template_key}
                </span>
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {updateMutation.isError && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 p-4">
                  <p className="text-sm font-medium text-rose-800 dark:text-rose-300">
                    {getErrorMessage(updateMutation.error)}
                  </p>
                </div>
              )}

              <div className="bg-primary-50/60 dark:bg-primary-950/30 p-4 rounded-xl border border-primary-200/80 dark:border-primary-900/60">
                <div className="flex items-center gap-2 mb-2">
                  <Code className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  <span className="text-xs font-bold uppercase tracking-wider text-primary-900 dark:text-primary-300">
                    Variáveis Disponíveis
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {editingTemplate.variables.map((v) => (
                    <span
                      key={v}
                      className="px-2 py-0.5 rounded bg-white dark:bg-slate-800 text-primary-700 dark:text-primary-300 font-mono text-xs font-semibold border border-primary-200 dark:border-primary-800"
                    >
                      {'{'}
                      {v}
                      {'}'}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-primary-700/80 dark:text-primary-400/80">
                  Insira as variáveis no texto HTML ou simples exatamente como apresentado acima.
                </p>
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                >
                  Assunto do Email *
                </label>
                <input
                  id="subject"
                  type="text"
                  required
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                />
              </div>

              <div>
                <label
                  htmlFor="body_html"
                  className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                >
                  Corpo em HTML *
                </label>
                <textarea
                  id="body_html"
                  required
                  className="w-full p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-mono text-xs focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                  rows={10}
                  value={formData.body_html}
                  onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                />
              </div>

              <div>
                <label
                  htmlFor="body_text"
                  className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                >
                  Corpo em Texto Simples *
                </label>
                <textarea
                  id="body_text"
                  required
                  className="w-full p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                  rows={6}
                  value={formData.body_text}
                  onChange={(e) => setFormData({ ...formData, body_text: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2.5 px-4 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-sm transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="flex-1 btn-primary py-2.5 text-sm font-semibold flex items-center justify-center gap-2 shadow-md"
                >
                  {updateMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> A guardar modelo...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Guardar Modelo
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
